import type { Option } from "@tento-lona/sheets";
import "@tento-ui/components/round";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Round } from "@tento-ui/components/round";
import { Scrollbars } from "@tento-ui/components/scrollbars";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { UserCalendarList } from "@tento-lona/sheets-ui";
import { Network } from "@tento-lona/network";
import { ExpiredAuthCard } from "./expired-auth-card";

// The calendar-list region: a framed, scrollable list of the user's calendars
// with first-class empty/null states. Owns the background frame, the scroll
// container, the slotted list, and the empty state — driven entirely through a
// discriminated ViewModel so each state (including the null states) renders in
// isolation and is storybook-able without a live CalendarsClient.
@component({
  name: "lona-calendar-list-panel",
})
export class CalendarListPanel extends LonaWebComponent {
  private $emptyMessage: HTMLParagraphElement = this.$("empty-message");
  private $connectGoogle: HTMLButtonElement = this.$("connect-google");
  private onConnectGoogle: Option<EmptyFunction> = null;
  $calendarBg = this.hydrate<Round>("calendar-bg")
    .bind({
      ...Round.Props.MODAL,
      radius: 24,
    });

  constructor() {
    super();
    GESTURE_MANAGER.addPointerEvent(this.$connectGoogle, {
      onClick: () => this.onConnectGoogle?.(),
    });
  }

  static makeWith(vm: CalendarListPanel.ViewModel): CalendarListPanel {
    return CalendarListPanel.make().bind(vm);
  }

  bind(vm: CalendarListPanel.ViewModel): this {
    this.clearChildren();
    this.onConnectGoogle = null;

    switch (vm.kind) {
      case "list": {
        this.toggleAttribute("empty", false);
        const $list = UserCalendarList.makeWith(vm.calendars, vm.options);
        $list.slot = "calendar-list";
        this.appendChild($list);
        break;
      }
      case "expired-auth": {
        this.toggleAttribute("empty", false);
        const $card = ExpiredAuthCard.makeWith({
          source: vm.source,
          authId: vm.authId,
        });
        $card.slot = "calendar-list";
        $card.style.marginLeft = "0";
        this.appendChild($card);
        break;
      }
      case "no-account": {
        this.toggleAttribute("empty", true);
        this.onConnectGoogle = vm.onConnectGoogle;
        this.$emptyMessage.textContent =
          "No Google account connected — connect one to see your calendars.";
        this.$connectGoogle.style.display = "";
        break;
      }
      case "no-calendars": {
        this.toggleAttribute("empty", true);
        this.$emptyMessage.textContent =
          "No calendars found on your connected Google account.";
        this.$connectGoogle.style.display = "none";
        break;
      }
    }

    return this;
  }

  onRecycle(): void {
    super.onRecycle();
    this.onConnectGoogle = null;
  }

  static $styles: Style.Compat[] = [
    Scrollbars.$opt,
    Button.$style,
    css`
      :host {
        display: block;
        position: relative;
        width: 100%;
        max-width: 400px;
        max-height: 100%;
        --cell-height: 28px;
        --calendar-font-size: var(--text-10px);
        --section-font-size: var(--text-14px);
        --icon-size: 20px;
      }

      #scroll {
        max-height: 600px;
        padding: 16px;
        overflow-y: scroll;
        height: 100%;
      }

      #empty-state {
        display: none;
        align-items: center;
        text-align: center;
        gap: 16px;
        padding: 24px 16px;
      }

      :host([empty]) #empty-state {
        display: flex;
      }

      #empty-message {
        margin: 0;
        color: var(--text-sec);
        font-size: var(--text-14px);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-round id=calendar-bg floating></std-round>
    <div id=scroll>
      <slot name="calendar-list"></slot>
      <std-col id=empty-state>
        <p id=empty-message small></p>
        <button id=connect-google primary>Connect Google</button>
      </std-col>
    </div>
  `;
}

export namespace CalendarListPanel {
  export type ViewModel =
    | {
      kind: "list";
      calendars: readonly Network.CalendarList[];
      options: Option<UserCalendarList.Options>;
    }
    | { kind: "expired-auth"; source: string; authId: string }
    | { kind: "no-account"; onConnectGoogle: EmptyFunction }
    | { kind: "no-calendars" };
}
